/**
 * In-memory cache for fetched Tingbog / akt PDFs.
 *
 * Closing the PDF viewer drops the blob from window params; this keeps the
 * decoded file so another servitut on the same akt does not wait for a
 * 500-page download again. In-flight fetches are shared. A waiter abort does
 * not cancel the download for others.
 *
 * Budget is tight on purpose: a scanned 500-page akt is often 30–150 MB, and
 * the open viewer also holds an ArrayBuffer plus rendered pages. Two files
 * covers bouncing between akts; the byte cap drops the older one if both are huge.
 */

export const TINGBOG_PDF_CACHE_MAX_ENTRIES = 2;
/** ~one large scanned akt, or two modest digital PDFs. */
export const TINGBOG_PDF_CACHE_MAX_BYTES = 80 * 1024 * 1024;

type CacheEntry = {
   promise: Promise<Blob>;
   settled: boolean;
   size: number;
};

const cache = new Map<string, CacheEntry>();
const lruKeys: string[] = [];

let maxEntries = TINGBOG_PDF_CACHE_MAX_ENTRIES;
let maxBytes = TINGBOG_PDF_CACHE_MAX_BYTES;

function abortError(signal?: AbortSignal): DOMException {
   const reason = signal?.reason;
   if (reason instanceof DOMException) {
      return reason;
   }
   return new DOMException("The operation was aborted.", "AbortError");
}

function removeLruKey(key: string) {
   const index = lruKeys.indexOf(key);
   if (index >= 0) {
      lruKeys.splice(index, 1);
   }
}

function touchLru(key: string) {
   removeLruKey(key);
   lruKeys.push(key);
}

function settledEntries(): CacheEntry[] {
   return lruKeys
      .map((key) => cache.get(key))
      .filter((entry): entry is CacheEntry => Boolean(entry?.settled));
}

function settledByteSize(): number {
   return settledEntries().reduce((sum, entry) => sum + entry.size, 0);
}

function shouldEvict(): boolean {
   const settled = settledEntries();
   if (settled.length > maxEntries) {
      return true;
   }
   return settled.length > 1 && settledByteSize() > maxBytes;
}

function evictSettledIfNeeded() {
   const newest = lruKeys[lruKeys.length - 1];
   while (shouldEvict()) {
      const oldest = lruKeys.find((key) => key !== newest && cache.get(key)?.settled);
      if (!oldest) {
         break;
      }
      removeLruKey(oldest);
      cache.delete(oldest);
   }
}

function bindAbort<T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> {
   if (!signal) {
      return promise;
   }
   if (signal.aborted) {
      return Promise.reject(abortError(signal));
   }
   return new Promise<T>((resolve, reject) => {
      const onAbort = () => reject(abortError(signal));
      signal.addEventListener("abort", onAbort, { once: true });
      promise.then(
         (value) => {
            signal.removeEventListener("abort", onAbort);
            if (signal.aborted) {
               onAbort();
               return;
            }
            resolve(value);
         },
         (error) => {
            signal.removeEventListener("abort", onAbort);
            if (signal.aborted) {
               onAbort();
               return;
            }
            reject(error);
         },
      );
   });
}

export function getCachedPdfBlob(
   key: string,
   loader: () => Promise<Blob>,
   signal?: AbortSignal,
): Promise<Blob> {
   if (signal?.aborted) {
      return Promise.reject(abortError(signal));
   }

   let entry = cache.get(key);
   if (!entry) {
      const created: CacheEntry = {
         promise: null as unknown as Promise<Blob>,
         settled: false,
         size: 0,
      };
      created.promise = loader().then(
         (blob) => {
            if (cache.get(key) === created) {
               created.settled = true;
               created.size = blob.size;
               touchLru(key);
               evictSettledIfNeeded();
            }
            return blob;
         },
         (error) => {
            if (cache.get(key) === created) {
               cache.delete(key);
               removeLruKey(key);
            }
            throw error;
         },
      );
      entry = created;
      cache.set(key, created);
      touchLru(key);
   } else if (entry.settled) {
      touchLru(key);
   }

   return bindAbort(entry.promise, signal);
}

export function clearTingbogPdfBlobCache() {
   cache.clear();
   lruKeys.length = 0;
}

export function tingbogPdfBlobCacheSize(): number {
   return cache.size;
}

export function setTingbogPdfBlobCacheLimitsForTests(limits: {
   maxEntries?: number;
   maxBytes?: number;
}) {
   maxEntries = limits.maxEntries ?? TINGBOG_PDF_CACHE_MAX_ENTRIES;
   maxBytes = limits.maxBytes ?? TINGBOG_PDF_CACHE_MAX_BYTES;
}
